import { useEffect, useState, useCallback } from "react";
import { fmtDate, fmtDateTime } from "../lib/date";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Breadcrumbs, ItemsWithin } from "../components/AssetContainment";
import { useAuth } from "../context/AuthContext";
import { StatusBanner, StatusBadge } from "../components/StatusBadge";
import { AssetNfcPanel, TagActionsDialog } from "../components/NfcTags";
import MoveToDialog from "../components/MoveToDialog";
import { CustodyPanel } from "../components/CustodyPanel";
import { RecordRepairButton, RecordRepairDialog } from "../components/RecordRepair";
import { MaintenanceBadge, MotBadge, TaxBadge } from "../components/MaintDims";
import AuditCard from "../components/AuditCard";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PhotoUpload, EvidenceThumb } from "../components/Evidence";
import EligibilityPanel from "../components/EligibilityPanel";
import RequirementsManager from "../components/RequirementsManager";
import AssetInsurancePanel from "../components/AssetInsurancePanel";
import RestrictionPanel from "../components/RestrictionPanel";
import RecordDeleteDialog from "../components/RecordDeleteDialog";
import { toast } from "sonner";
import { ClipboardText, WarningOctagon, Wrench, Gauge, ArrowClockwise, SlidersHorizontal, SealCheck, Car, Plus, Trash, GasPump, PencilSimple, Package, Truck, ClockCounterClockwise, CaretDown, CaretUp } from "@phosphor-icons/react";
import { PriorityBadge } from "../components/Priority";

const STATE_CLS = { up_to_date: "text-emerald-700", due_soon: "text-amber-700", due: "text-orange-700", overdue: "text-red-700" };

export const FUEL_TYPES = ["Petrol", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "LPG", "HVO", "Other"];
export const fuelClass = (t) => {
  const k = (t || "").toLowerCase();
  if (k === "petrol") return "bg-emerald-600 text-white";
  if (k === "diesel") return "bg-slate-900 text-white";
  if (k === "electric") return "bg-blue-600 text-white";
  if (k.includes("hybrid")) return "bg-teal-600 text-white";
  if (k === "lpg" || k === "hvo") return "bg-amber-500 text-slate-900";
  return "bg-slate-500 text-white";
};

const DOT_CLS = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500" };
const STATE_TONE = { valid: "green", taxed: "green", up_to_date: "green", expiring: "amber", due_soon: "amber", due: "amber", expired: "red", untaxed: "red", overdue: "red", sorn: "slate", unknown: "slate" };
const INS_TONE = { valid: "green", not_required: "slate", expired: "red", not_valid: "red" };
const INS_LBL = { valid: "Valid", not_required: "N/A", expired: "Expired", not_valid: "Missing" };
const MAINT_TONE = { up_to_date: "green", due_soon: "amber", due: "amber", overdue: "red" };
const MAINT_LBL = { up_to_date: "OK", due_soon: "Due Soon", due: "Due", overdue: "Overdue" };
const CHIP_CLS = { green: "bg-emerald-100 text-emerald-800 border-emerald-300", amber: "bg-amber-100 text-amber-900 border-amber-400", red: "bg-red-100 text-red-800 border-red-300", slate: "bg-slate-100 text-slate-700 border-slate-300" };

function StatusDot({ dot }) { return <span data-testid={`status-dot-${dot}`} className={`inline-block h-3.5 w-3.5 rounded-full ${DOT_CLS[dot] || "bg-slate-400"}`} />; }
function RegPlate({ reg }) {
  if (!reg) return null;
  return <span data-testid="asset-reg-plate" className="inline-flex items-center font-mono font-black tracking-widest text-lg bg-yellow-400 text-black px-3 py-1 rounded-md border-2 border-yellow-500 shadow-sm">{reg}</span>;
}
const METER_MILES = ["miles", "km"];
const METER_HOURS = ["engine_hours", "operating_hours", "hours"];
const METER_SOURCE_LABEL = { daily_check: "driver check", maintenance: "maintenance job", manual: "manual entry", import: "import", admin_correction: "admin correction", telemetry: "telematics" };
function LastKnownMeter({ asset }) {
  const u = asset.meter_unit || asset.measurement_unit;
  const isMiles = METER_MILES.includes(u);
  const isHours = METER_HOURS.includes(u);
  if (!isMiles && !isHours) return null;
  const val = isMiles ? asset.current_mileage : asset.current_hours;
  const unitLabel = isMiles ? (u === "km" ? "km" : "mi") : "h";
  const noun = isMiles ? "Mileage" : "Hours";
  if (val == null) {
    return <div data-testid="asset-last-meter" className="font-body text-sm text-slate-400 italic mt-1">{noun} not recorded</div>;
  }
  const lm = asset.last_meter;
  const src = lm ? (METER_SOURCE_LABEL[lm.source] || lm.source) : null;
  return (
    <div data-testid="asset-last-meter" className="font-body text-sm text-slate-600 mt-1">
      Last known {noun.toLowerCase()}: <span className="font-mono font-bold text-slate-800">{Number(val).toLocaleString("en-GB")} {unitLabel}</span>
      {lm && (src || lm.reading_at) && (
        <span className="text-slate-400"> · {src}{lm.reading_at ? ` · ${fmtDateTime(lm.reading_at)}` : ""}</span>
      )}
    </div>
  );
}
function Chip({ tone, label, testid }) {
  return <span data-testid={testid} className={`text-xs font-bold uppercase tracking-wide rounded px-2 py-1 border ${CHIP_CLS[tone] || CHIP_CLS.slate}`}>{label}</span>;
}
const motLbl = (m) => ({ valid: "Valid", expiring: "Expiring", expired: "Expired" }[m.state] || "—");
const taxLbl = (t) => ({ taxed: "Valid", due_soon: "Due Soon", untaxed: "Untaxed", sorn: "SORN" }[t.state] || "—");

function PartCard({ it, tone, extra }) {
  const cls = { fits: "border-emerald-200", might: "border-amber-200", reserved: "border-indigo-200" }[tone] || "border-slate-200";
  return (
    <div className={`bg-white border-2 ${cls} rounded-lg p-3`} data-testid={`asset-part-${it.id}`}>
      <div className="font-body font-semibold text-slate-800">{it.name} {it.part_number ? <span className="text-xs text-slate-400 font-mono">{it.part_number}</span> : null}</div>
      <div className="text-sm text-slate-600"><b>{it.available}</b> available{it.reserved > 0 ? ` · ${it.reserved} reserved` : ""} {it.unit !== "each" ? it.unit : ""} · {it.location_label || "no location"}</div>
      {extra}
    </div>
  );
}

function AllocateExistingDialog({ assetId, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState("fits");
  const [qty, setQty] = useState(1);
  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/maintenance/stock/items?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const apply = async () => {
    if (!sel) { toast.error("Select a part"); return; }
    try {
      if (status === "reserved") {
        if (!(Number(qty) > 0)) { toast.error("Enter a quantity"); return; }
        await api.post(`/maintenance/stock/items/${sel.id}/reserve`, { asset_id: assetId, quantity: Number(qty), reason: "Bought/reserved for this vehicle" });
      } else {
        await api.post(`/maintenance/stock/items/${sel.id}/fitment`, { target_type: "asset", target_id: assetId, status });
      }
      toast.success("Part allocated to this asset"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="allocate-existing-dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-black">Allocate existing stock</h3><button data-testid="allocate-close" onClick={onClose}><Plus size={20} className="rotate-45" /></button></div>
        <Input data-testid="allocate-search" placeholder="Search stock parts…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 mb-1" />
        {results.map((it) => (
          <button key={it.id} data-testid={`allocate-result-${it.id}`} onClick={() => setSel(it)} className={`block w-full text-left text-sm px-2 py-1.5 rounded mt-1 ${sel?.id === it.id ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
            {it.name} — <b>{it.available}</b> available
          </button>
        ))}
        {sel && <div className="mt-3 border-t border-slate-200 pt-3">
          <div className="text-sm font-semibold mb-2">{sel.name}</div>
          <select data-testid="allocate-status" className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm mb-2" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="fits">Confirmed Fit</option>
            <option value="might_fit">Might Fit</option>
            <option value="reserved">Bought / Reserved for this asset</option>
          </select>
          {status === "reserved" && <Input data-testid="allocate-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Quantity to reserve" className="h-9 mb-2" />}
          <Button data-testid="allocate-apply" onClick={apply} className="h-10 w-full bg-blue-600 hover:bg-blue-700 font-semibold">Allocate</Button>
        </div>}
      </div>
    </div>
  );
}

function AssetFaultsPanel({ assetId, canJob, canSignoff, canReport }) {
  const nav = useNavigate();
  const [faults, setFaults] = useState(null);
  const load = useCallback(() => { api.get(`/faults?asset_id=${assetId}`).then((r) => setFaults(r.data)).catch(() => setFaults([])); }, [assetId]);
  useEffect(() => { load(); }, [load]);
  const startRepair = async (f) => {
    try {
      const j = await api.post("/maintenance/jobs", {
        asset_id: assetId, title: `Repair: ${f.title || f.description}`,
        priority: f.priority || "medium", description: f.description, source_fault_id: f.id,
      });
      await api.put(`/faults/${f.id}`, { repair_status: "in_progress" });
      toast.success("Repair job created and linked");
      nav(`/maintenance/jobs/${j.data.id}?fault=${f.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.job_id) {
        // Already on an active job, or already repaired on a completed job — open it, never duplicate.
        toast.info(d.message || "This fault already has a job card.");
        nav(`/maintenance/jobs/${d.job_id}`);
      } else {
        toast.error(typeof d === "string" ? d : "Failed");
      }
    }
  };
  if (!faults) return <p className="text-slate-400 text-sm p-2">Loading faults…</p>;
  const open = faults.filter((f) => !f.final_signoff_at);
  return (
    <div className="flex flex-col gap-3" data-testid="asset-faults-panel">
      <div className="flex items-center justify-between">
        <span className="font-body font-semibold text-slate-800">{open.length ? `⚠ ${open.length} open fault${open.length > 1 ? "s" : ""}` : "No open faults"}</span>
        {canReport && <Button data-testid="asset-report-fault" onClick={() => nav(`/faults/report?asset=${assetId}`)} className="h-9 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"><Plus size={15} weight="bold" className="mr-1" />Report Fault</Button>}
      </div>
      {faults.map((f) => (
        <div key={f.id} className="bg-white border-2 border-slate-200 rounded-lg p-3" data-testid={`asset-fault-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><PriorityBadge priority={f.priority} /><span className="font-body font-semibold text-slate-800">{f.title || f.description}</span></div>
              {f.title && f.description && <p className="text-sm text-slate-600 mt-0.5">{f.description}</p>}
              <p className="font-mono text-xs text-slate-500 mt-1">
                {f.severity_name ? `${f.severity_name} · ` : ""}{f.status_name || (f.final_signoff_at ? "Closed" : "Open")}
                {f.reporter_name ? ` · by ${f.reporter_name}` : ""} · {fmtDate(f.reported_at)}
                {f.assignee_name ? ` · ${f.assignee_name}` : ""}
              </p>
            </div>
          </div>
          {!f.final_signoff_at && <div className="mt-2"><RecordRepairButton assetId={f.asset_id} faultId={f.id} testid={`asset-fault-repair-${f.id}`} onDone={load} className="h-8 text-xs" /></div>}
          {(canJob || canSignoff) && !f.final_signoff_at && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {canJob && <Button data-testid={`fault-start-repair-${f.id}`} onClick={() => startRepair(f)} className="h-8 bg-blue-600 hover:bg-blue-700 font-semibold text-xs">Start Repair (create job)</Button>}
              {canSignoff && <Button data-testid={`fault-signoff-${f.id}`} variant="outline" onClick={async () => { try { await api.post(`/faults/${f.id}/signoff`, { comments: "Repaired from asset page" }); toast.success("Fault signed off"); load(); } catch (e) { toast.error("Failed"); } }} className="h-8 font-semibold text-xs text-emerald-700 border-emerald-300">Mark Repaired</Button>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AssetPartsPanel({ assetId, canManage }) {
  const [data, setData] = useState(null);
  const [alloc, setAlloc] = useState(false);
  const reload = useCallback(() => { api.get(`/maintenance/stock/for-asset/${assetId}`).then((r) => setData(r.data)).catch(() => setData({ confirmed: [], possible: [], reserved: [], recently_fitted: [] })); }, [assetId]);
  useEffect(() => { reload(); }, [reload]);
  const releaseRes = async (rid) => { try { await api.post(`/maintenance/stock/reservations/${rid}/release`); toast.success("Reservation released"); reload(); } catch (e) { toast.error("Failed"); } };
  if (!data) return <p className="text-slate-400 text-sm p-2">Loading parts…</p>;
  const empty = !data.confirmed.length && !data.possible.length && !data.reserved.length && !data.recently_fitted.length;
  return (
    <div className="flex flex-col gap-4" data-testid="asset-parts-panel">
      {canManage && <div><Button data-testid="allocate-existing-open" onClick={() => setAlloc(true)} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold"><Plus size={15} weight="bold" className="mr-1" />Allocate Existing Stock</Button></div>}
      {empty && <p className="text-slate-400 text-sm" data-testid="asset-parts-empty">No parts linked to this asset yet. Use “Allocate Existing Stock”, or set fitment from Parts &amp; Stock.</p>}
      {data.confirmed.length > 0 && <div>
        <div className="font-body font-semibold text-emerald-700 mb-2 flex items-center gap-1"><SealCheck size={16} weight="bold" />Confirmed fit</div>
        <div className="grid sm:grid-cols-2 gap-2">{data.confirmed.map((it) => <PartCard key={it.id} it={it} tone="fits" />)}</div>
      </div>}
      {data.possible.length > 0 && <div>
        <div className="font-body font-semibold text-amber-700 mb-2 flex items-center gap-1"><Package size={16} weight="bold" />Might fit — verify before fitting</div>
        <div className="grid sm:grid-cols-2 gap-2">{data.possible.map((it) => <PartCard key={it.id} it={it} tone="might" />)}</div>
      </div>}
      {data.reserved.length > 0 && <div>
        <div className="font-body font-semibold text-indigo-700 mb-2 flex items-center gap-1"><Truck size={16} weight="bold" />Bought / reserved for this vehicle</div>
        <div className="grid sm:grid-cols-2 gap-2">{data.reserved.map((it) => <PartCard key={it.id + it.reservation_id} it={it} tone="reserved" extra={
          <div className="text-xs text-indigo-700 font-semibold mt-0.5 flex items-center justify-between">
            <span>{it.reserved_quantity} reserved{it.reservation_reason ? ` — ${it.reservation_reason}` : ""}</span>
            {canManage && <button data-testid={`asset-res-release-${it.reservation_id}`} onClick={() => releaseRes(it.reservation_id)} className="text-red-600">Release</button>}
          </div>
        } />)}</div>
      </div>}
      {data.recently_fitted.length > 0 && <div>
        <div className="font-body font-semibold text-slate-700 mb-2 flex items-center gap-1"><ClockCounterClockwise size={16} weight="bold" />Recently fitted</div>
        <div className="flex flex-col gap-1">{data.recently_fitted.map((r) => (
          <div key={r.id} className="text-sm text-slate-700 border-b border-slate-100 py-1 flex justify-between" data-testid={`asset-part-fitted-${r.id}`}>
            <span>{r.quantity} × {r.name}{r.part_number ? ` (${r.part_number})` : ""}</span>
            <span className="font-mono text-xs text-slate-500">{fmtDate(r.date)}</span>
          </div>
        ))}</div>
      </div>}
      {alloc && <AllocateExistingDialog assetId={assetId} onClose={() => setAlloc(false)} onDone={() => { setAlloc(false); reload(); }} />}
    </div>
  );
}


const DISPOSAL_OPTIONS = [["sold", "Sold"], ["scrapped", "Scrapped"], ["written_off", "Written off"], ["transferred", "Transferred"], ["lost", "Lost / Stolen"], ["other", "Other"]];
const DISPOSAL_LABELS = Object.fromEntries(DISPOSAL_OPTIONS);

export default function AssetDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm, isSuperAdmin } = useAuth();
  const canManageAsset = hasPerm("asset_manager", "administrator");
  const canMaint = hasPerm("maintenance", "supervisor", "administrator");
  const isAdmin = hasPerm("administrator");
  const canAudit = hasPerm("administrator", "auditor", "supervisor");
  const canViewTags = hasPerm("nfc_manage", "asset_manager", "supervisor", "administrator", "auditor", "super_admin");
  const canManageNfc = hasPerm("nfc_manage", "asset_manager", "administrator", "super_admin");
  const canReplaceRevokeNfc = hasPerm("nfc_replace_revoke", "asset_manager", "administrator", "super_admin");
  const canSeeCompliance = hasPerm("asset_manager", "maintenance", "supervisor", "administrator");
  const [asset, setAsset] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [checks, setChecks] = useState([]);
  const [faults, setFaults] = useState([]);
  const [quick, setQuick] = useState([]);
  const [history, setHistory] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tags, setTags] = useState([]);
  const [statusDlg, setStatusDlg] = useState(false);
  const [rtsDlg, setRtsDlg] = useState(false);
  const [roadDlg, setRoadDlg] = useState(false);
  const [editDlg, setEditDlg] = useState(false);
  const [tagDlg, setTagDlg] = useState(false);
  const [moveDlg, setMoveDlg] = useState(false);
  const [repairDlg, setRepairDlg] = useState(false);
  const [actionTag, setActionTag] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [rts, setRts] = useState({ test_result: "", comments: "", target_status_code: "available" });
  const [disposeDlg, setDisposeDlg] = useState(false);
  const [disp, setDisp] = useState({ method: "sold", date: new Date().toISOString().slice(0, 10), proceeds: "", disposed_to: "", reason: "" });
  const [showBasis, setShowBasis] = useState(false);
  const [delDlg, setDelDlg] = useState(false);

  const load = useCallback(() => {
    api.get(`/assets/${id}`).then((r) => setAsset(r.data)).catch(() => {});
    api.get(`/checks/completed?asset_id=${id}`).then((r) => setChecks(r.data)).catch(() => {});
    api.get(`/faults?asset_id=${id}`).then((r) => setFaults(r.data)).catch(() => {});
    api.get(`/quickdata/resolve?asset_id=${id}`).then((r) => setQuick(r.data)).catch(() => {});
    api.get(`/assets/${id}/maintenance-history`).then((r) => setHistory(r.data)).catch(() => {});
    if (canAudit) api.get(`/audit?entity_type=assets&entity_id=${id}`).then((r) => setAudit(r.data)).catch(() => {});
  }, [id, canAudit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (hasPerm("supervisor", "administrator")) api.get("/config/asset-statuses").then((r) => setStatuses(r.data)).catch(() => {});
    if (hasPerm("maintenance", "supervisor", "administrator")) api.get(`/maintenance/schedules?asset_id=${id}`).then((r) => setSchedules(r.data)).catch(() => {});
    if (hasPerm("nfc_manage", "asset_manager", "supervisor", "administrator", "auditor", "super_admin")) api.get(`/tags?target_id=${id}`).then((r) => setTags(r.data)).catch(() => {});
  }, [hasPerm, id]);

  const reloadSchedules = () => api.get(`/maintenance/schedules?asset_id=${id}`).then((r) => setSchedules(r.data)).catch(() => {});
  const reloadTags = () => api.get(`/tags?target_id=${id}`).then((r) => setTags(r.data)).catch(() => {});

  if (!asset) return <p className="text-slate-500">Loading…</p>;

  const ms = asset.maintenance_status;
  const rc = asset.road_compliance;
  const eff = asset.effective_status || {};

  const changeStatus = async () => {
    try { await api.post(`/assets/${id}/status`, { status_id: newStatus, reason }); toast.success("Status updated"); setStatusDlg(false); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doRts = async () => {
    const awaiting = faults.filter((f) => f.repair_status === "repaired_awaiting_signoff" && !f.final_signoff_at);
    const body = awaiting.length === 1 ? { ...rts, fault_id: awaiting[0].id } : rts;
    try { await api.post(`/assets/${id}/return-to-service`, body); toast.success("Asset returned to service"); setRtsDlg(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doDispose = async () => {
    if (!disp.method) { toast.error("Select a disposal method"); return; }
    try {
      const r = await api.post(`/assets/${id}/dispose`, { ...disp, proceeds: disp.proceeds === "" ? null : Number(disp.proceeds) });
      const extra = (r.data.faults_closed || r.data.jobs_cancelled) ? ` · closed ${r.data.faults_closed} fault(s), ${r.data.jobs_cancelled} job(s)` : "";
      toast.success(`Asset disposed & archived${extra}`);
      setDisposeDlg(false); nav("/assets");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doRestore = async () => {
    try { await api.post(`/assets/${id}/restore`, {}); toast.success("Asset restored to register"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doDeleteAsset = () => setDelDlg(true);

  return (
    <div className="flex flex-col gap-5 max-w-4xl">
      {eff.code && (() => {
        const hasBasis = (asset.restriction || eff.restriction_missing || eff.recorded_status === "restricted_use") && !asset.archived;
        return (
        <div data-testid="asset-status-banner"
          onClick={hasBasis ? () => setShowBasis((v) => !v) : undefined}
          role={hasBasis ? "button" : undefined}
          className={`w-full rounded-lg ${eff.dot === "red" ? "bg-red-600 text-white" : eff.dot === "amber" ? "bg-amber-400 text-slate-900" : "bg-emerald-600 text-white"} py-5 px-4 text-center ${hasBasis ? "cursor-pointer transition-opacity hover:opacity-95" : ""}`}>
          <div className="font-display text-2xl sm:text-3xl font-black uppercase tracking-widest">{eff.dot === "green" ? "Available for use" : eff.name}</div>
          {eff.dot === "red" && eff.blocked && <div className="font-display text-sm font-black uppercase tracking-widest mt-1">Do Not Use</div>}
          {eff.reasons?.length > 0 && <p data-testid="asset-banner-reasons" className="mt-2 font-body text-sm font-semibold">Reason: {eff.reasons.join(" · ")}</p>}
          {hasBasis && (
            <div data-testid="asset-basis-toggle" className="mt-2 inline-flex items-center gap-1 font-body text-xs font-bold uppercase tracking-wide opacity-80">
              {showBasis ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
              {showBasis ? "Hide documented basis" : "Tap for documented basis"}
            </div>
          )}
        </div>
        );
      })()}
      {delDlg && (
        <RecordDeleteDialog entity="assets" id={id} label={asset.asset_number || asset.name} isSuperAdmin={isSuperAdmin}
          onClose={() => setDelDlg(false)} onDeleted={() => nav("/assets")} />
      )}
      {showBasis && (asset.restriction || eff.restriction_missing || eff.recorded_status === "restricted_use") && !asset.archived && (
        <RestrictionPanel asset={asset} canAuthorise={hasPerm("supervisor", "administrator")} onChange={load} />
      )}
      {asset.archived && (
        <div data-testid="disposal-banner" className="bg-slate-900 text-white rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display font-black uppercase text-sm tracking-wide">Disposed / Archived</p>
            <p className="font-mono text-xs text-slate-300 mt-1">
              {DISPOSAL_LABELS[asset.disposal_method] || asset.disposal_method}
              {asset.disposal_date ? ` · ${fmtDate(asset.disposal_date)}` : ""}
              {asset.disposal_proceeds != null ? ` · £${Number(asset.disposal_proceeds).toFixed(2)}` : ""}
              {asset.disposal_to ? ` · to ${asset.disposal_to}` : ""}
              {asset.disposed_by_name ? ` · by ${asset.disposed_by_name}` : ""}
            </p>
            {asset.disposal_reason && <p className="text-xs text-slate-400 mt-1">{asset.disposal_reason}</p>}
          </div>
          {canManageAsset && <Button data-testid="restore-asset" onClick={doRestore} className="bg-emerald-600 hover:bg-emerald-700 font-bold">Restore to register</Button>}
        </div>
      )}

      {/* Area 1 — Identity + effective status */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span data-testid="asset-number" className="font-display text-2xl sm:text-3xl font-black text-slate-400">{asset.asset_number}</span>
            <h1 data-testid="asset-name" className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">{asset.name}</h1>
            {asset.asset_type_name && <span className="font-body text-base text-slate-500 font-semibold">· {asset.asset_type_name}</span>}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <RegPlate reg={asset.registration} />
            <StatusDot dot={eff.dot} />
          </div>
          <LastKnownMeter asset={asset} />
          {asset.assigned_person_name && <div className="font-body text-sm text-slate-500 mt-1" data-testid="asset-assigned-to">Assigned to: <span className="font-semibold text-slate-700">{asset.assigned_person_name}</span></div>}
          <div className="mt-2 flex"><CustodyPanel asset={asset} onChange={load} /></div>
        </div>
        {canManageAsset && (
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="edit-asset" onClick={() => setEditDlg(true)} className="h-8 bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold text-xs"><PencilSimple size={14} weight="bold" className="mr-1" />Edit details</Button>
            {!asset.archived && <Button data-testid="dispose-asset" onClick={() => setDisposeDlg(true)} className="h-8 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-semibold text-xs">Sell / Dispose</Button>}
            {isAdmin && <Button data-testid="delete-asset-permanent" onClick={doDeleteAsset} className="h-8 bg-red-600 text-white hover:bg-red-700 font-semibold text-xs"><Trash size={14} weight="bold" className="mr-1" />Delete</Button>}
          </div>
        )}
      </div>

      {/* Compact summary chips — status shown once via banner + dot, so only compliance/fuel chips here */}
      <div className="flex flex-wrap items-center gap-2" data-testid="asset-summary-chips">
        {ms?.status && ms.status !== "up_to_date" && <Chip tone={MAINT_TONE[ms.status] || "slate"} label={`Maint: ${MAINT_LBL[ms.status] || ms.status}`} testid="chip-maint" />}
        {rc?.mot?.required && <Chip tone={STATE_TONE[rc.mot.state] || "slate"} label={`MOT ${motLbl(rc.mot)}`} testid="chip-mot" />}
        {rc?.tax?.applicable && <Chip tone={STATE_TONE[rc.tax.state] || "slate"} label={`Tax ${taxLbl(rc.tax)}`} testid="chip-tax" />}
        {asset.insurance && <Chip tone={INS_TONE[asset.insurance.state] || "slate"} label={`Insurance ${INS_LBL[asset.insurance.state] || asset.insurance.state}`} testid="chip-insurance" />}
        {asset.fuel_type && <span data-testid="fuel-badge" className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide rounded px-2 py-1 ${fuelClass(asset.fuel_type)}`}><GasPump size={14} weight="bold" />{asset.fuel_type}</span>}
      </div>

      {asset.effective_location && (asset.contained_in || asset.effective_location.location_name) && (
        <div data-testid="effective-location" className="bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
          <span className="font-semibold text-slate-700">Effective location: </span>
          <span className="text-slate-800">
            {asset.contained_in && `in ${asset.contained_in.asset_number} — ${asset.contained_in.name}`}
            {asset.effective_location.chain?.length > 0 && ` → ${asset.effective_location.chain.map((c) => c.asset_number).join(" → ")}`}
            {asset.effective_location.location_name ? `${asset.contained_in ? " → " : ""}${asset.effective_location.location_name}` : ""}
          </span>
        </div>
      )}
      {asset.effective_location && <LoadoutHistory vehicleId={asset.id} />}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Breadcrumbs asset={asset} />
      </div>
      <ItemsWithin assetId={asset.id} />

      <Dialog open={disposeDlg} onOpenChange={setDisposeDlg}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Sell / Dispose Asset</DialogTitle>
            <DialogDescription>This archives the asset off-screen and closes any open faults/jobs. You can restore it later.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-semibold text-slate-600">Method</label>
          <select data-testid="dispose-method" value={disp.method} onChange={(e) => setDisp({ ...disp, method: e.target.value })} className="h-11 border-2 border-slate-300 rounded px-2">
            {DISPOSAL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-600">Date</label>
          <Input data-testid="dispose-date" type="date" value={disp.date} onChange={(e) => setDisp({ ...disp, date: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Sale price / proceeds (£)</label>
          <Input data-testid="dispose-proceeds" type="number" step="0.01" placeholder="0.00" value={disp.proceeds} onChange={(e) => setDisp({ ...disp, proceeds: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Buyer / recipient</label>
          <Input data-testid="dispose-to" placeholder="Who is it going to?" value={disp.disposed_to} onChange={(e) => setDisp({ ...disp, disposed_to: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Reason / notes</label>
          <Textarea data-testid="dispose-reason" value={disp.reason} onChange={(e) => setDisp({ ...disp, reason: e.target.value })} />
          <Button data-testid="dispose-confirm" onClick={doDispose} className="bg-red-600 hover:bg-red-700 font-bold uppercase">Confirm Disposal</Button>
        </DialogContent>
      </Dialog>

      {/* Reference photo */}
      <div className="flex items-center gap-4">
        {asset.photo_url && <EvidenceThumb fileId={asset.photo_url} />}
        {canManageAsset && (
          <PhotoUpload category="reference" entityType="asset" entityId={asset.id} label={asset.photo_url ? "Replace Photo" : "Add Photo"}
            onUploaded={async (f) => { try { await api.put(`/assets/${asset.id}`, { photo_url: f.id }); toast.success("Photo saved"); load(); } catch (e) { toast.error("Failed"); } }} />
        )}
      </div>

      {/* Notices */}
      {asset.notices?.length > 0 && (
        <div className="flex flex-col gap-2">
          {asset.notices.map((n) => (
            <div key={n.id} data-testid={`notice-${n.id}`}
              className={`px-4 py-2 rounded border-l-8 ${n.priority === "critical" ? "bg-red-50 border-red-600" : n.priority === "warning" ? "bg-amber-50 border-amber-500" : "bg-slate-50 border-slate-400"}`}>
              <span className="font-body text-sm text-slate-800">{n.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions — one consistent card size/style across the operational grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Button data-testid="action-start-check" onClick={() => nav(`/assets/${id}/check`)} className="h-16 text-base font-bold uppercase bg-blue-600 hover:bg-blue-700 flex-col gap-1"><ClipboardText size={22} weight="bold" /> Start Check</Button>
        <Button data-testid="action-report-fault" onClick={() => nav(`/faults/report?asset=${id}`)} className="h-16 text-base font-bold uppercase bg-amber-500 hover:bg-amber-600 text-slate-900 flex-col gap-1"><WarningOctagon size={22} weight="bold" /> Report Fault</Button>
        <Button data-testid="action-record-repair" onClick={() => setRepairDlg(true)} className="h-16 text-base font-bold uppercase bg-emerald-700 hover:bg-emerald-800 flex-col gap-1"><Wrench size={22} weight="bold" /> Record Repair</Button>
        {repairDlg && <RecordRepairDialog assetId={id} onClose={() => setRepairDlg(false)} onDone={() => { setRepairDlg(false); window.location.reload(); }} />}
        <Button data-testid="action-move-asset" onClick={() => setMoveDlg(true)} className="h-16 text-base font-bold uppercase bg-indigo-600 hover:bg-indigo-700 flex-col gap-1"><Truck size={22} weight="bold" /> Move This Asset</Button>
        <Button data-testid="action-add-items" disabled title="Available when the movement rework ships" className="h-16 text-base font-bold uppercase bg-slate-200 text-slate-400 cursor-not-allowed flex-col gap-0.5"><Plus size={22} weight="bold" /> Add Items<span className="text-[9px] normal-case font-semibold tracking-normal">Coming soon</span></Button>
        <Button data-testid="action-transfer-items" disabled title="Available when the movement rework ships" className="h-16 text-base font-bold uppercase bg-slate-200 text-slate-400 cursor-not-allowed flex-col gap-0.5"><Package size={22} weight="bold" /> Transfer Items<span className="text-[9px] normal-case font-semibold tracking-normal">Coming soon</span></Button>
        {hasPerm("supervisor", "administrator") && <Button data-testid="action-change-status" onClick={() => setStatusDlg(true)} className="h-16 text-base font-bold uppercase bg-slate-900 hover:bg-slate-800 flex-col gap-1"><SlidersHorizontal size={22} weight="bold" /> Set Status</Button>}
        {hasPerm("return_to_service", "supervisor", "administrator") && <Button data-testid="action-return-to-service" onClick={() => setRtsDlg(true)} className="h-16 text-base font-bold uppercase bg-emerald-600 hover:bg-emerald-700 flex-col gap-1"><ArrowClockwise size={22} weight="bold" /> Return to Service</Button>}
      </div>
      {/* Back-office meter reading (authorised managers) — the standalone Reading quick-action was removed;
          readings are captured through vehicle/plant checks and maintenance completion. */}
      {hasPerm("supervisor", "administrator", "asset_manager") && (
        <div className="flex" data-testid="backoffice-reading"><ReadingButton id={id} asset={asset} canForce={hasPerm("supervisor", "administrator")} onDone={load} /></div>
      )}

      {/* Area 3 — Compliance detail (only the cards that apply to this asset type) — hidden for basic users */}
      {canSeeCompliance && (rc || asset.insurance || asset.loler?.required) && (
        <div data-testid="compliance-card" className="bg-white border-2 border-slate-300 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-200 bg-slate-50">
            <h2 className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><SealCheck size={20} weight="bold" />Compliance</h2>
            {canManageAsset && rc && <Button data-testid="edit-road" onClick={() => setRoadDlg(true)} className="h-9 bg-slate-900 font-semibold">Edit MOT / Tax</Button>}
          </div>
          {(asset.compliance_locks || []).length > 0 && (
            <div data-testid="compliance-locks" className="mx-4 mt-3 bg-red-50 border-l-8 border-red-600 rounded p-3">
              <div className="font-display font-black text-red-700 uppercase text-xs tracking-wide">Out of Service — compliance lock{asset.compliance_locks.length > 1 ? "s" : ""}</div>
              <ul className="mt-1 text-sm text-red-800 list-disc pl-5">
                {(asset.compliance_locks || []).map((l) => <li key={l.code} data-testid={`lock-${l.code}`}>{l.reason}</li>)}
              </ul>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4 p-4">
            {rc && (
              <div data-testid="compliance-mot">
                <div className="flex items-center gap-2 mb-1 flex-wrap"><SealCheck size={18} weight="bold" className="text-slate-500 shrink-0" /><span className="font-body font-semibold">MOT</span>{rc?.mot?.required && <MotBadge mot={rc.mot} hideDate />}</div>
                {rc?.mot?.required ? (
                  <div className="font-mono text-xs text-slate-600 space-y-0.5">
                    <div>Expiry: {fmtDate(rc.mot.expiry) || "—"}</div>
                    {rc.mot.reference && <div>Ref: {rc.mot.reference}</div>}
                    {rc.mot.cert_file_id && <div className="pt-1"><EvidenceThumb fileId={rc.mot.cert_file_id} /></div>}
                  </div>
                ) : <div className="font-body text-sm text-slate-400">Not required</div>}
              </div>
            )}
            {rc && (
              <div data-testid="compliance-tax">
                <div className="flex items-center gap-2 mb-1 flex-wrap"><Car size={18} weight="bold" className="text-slate-500 shrink-0" /><span className="font-body font-semibold whitespace-nowrap">Road Tax</span>{rc?.tax?.applicable && <TaxBadge tax={rc.tax} />}</div>
                {rc?.tax?.applicable ? (
                  <div className="font-mono text-xs text-slate-600 space-y-0.5">
                    <div>Due: {fmtDate(rc.tax.due_date) || "—"}</div>
                    {rc.tax.state === "sorn" && <div className="font-bold text-slate-700">SORN declared</div>}
                  </div>
                ) : <div className="font-body text-sm text-slate-400">Not applicable</div>}
              </div>
            )}
            <div data-testid="compliance-insurance">
              <AssetInsurancePanel asset={asset} isAdmin={isAdmin} onSaved={load} />
            </div>
            {asset.loler?.required && (
              <div data-testid="compliance-loler">
                <div className="flex items-center gap-2 mb-1"><SealCheck size={18} weight="bold" className="text-indigo-500" /><span className="font-body font-semibold">LOLER</span>
                  <span className={`text-[10px] font-black uppercase rounded px-1.5 py-0.5 ${asset.loler.state === "valid" ? "bg-emerald-100 text-emerald-700" : asset.loler.state === "due_soon" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{asset.loler.label || asset.loler.state}</span>
                </div>
                <div className="font-mono text-xs text-slate-600 space-y-0.5">
                  <div>Last: {fmtDate(asset.loler.last_exam) || "—"}</div>
                  <div>Next due: {fmtDate(asset.loler.next_due) || "—"}</div>
                </div>
                <a href="/loler" data-testid="asset-loler-link" className="font-body text-xs text-indigo-600 font-semibold">Open LOLER →</a>
              </div>
            )}
          </div>
        </div>
      )}

      <Tabs defaultValue="maintenance">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="checks" data-testid="tab-checks">Checks</TabsTrigger>
          <TabsTrigger value="faults" data-testid="tab-faults">Faults</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quickdata">Quick Data</TabsTrigger>
          <TabsTrigger value="parts" data-testid="tab-parts">Parts</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">Usage</TabsTrigger>
          <TabsTrigger value="eligibility" data-testid="tab-eligibility">Who Can Use</TabsTrigger>
          {(canManageAsset || canMaint) && <TabsTrigger value="requirements" data-testid="tab-requirements">Requirements</TabsTrigger>}
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          {canViewTags && <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>}
        </TabsList>

        <TabsContent value="maintenance">
          {/* Live maintenance status per trigger */}
          {ms?.triggers?.length > 0 && (
            <div className="bg-white border-2 border-slate-200 rounded-lg p-3 mb-3">
              <div className="font-body font-semibold text-slate-800 mb-2">Service status <span className={`font-bold ${STATE_CLS[ms.status] || ""}`}>{ms.label}</span></div>
              {ms.triggers.map((t, i) => (
                <div key={i} data-testid={`maint-trigger-${i}`} className="flex items-center justify-between text-sm border-b last:border-0 border-slate-100 py-1.5">
                  <span className="font-body text-slate-700">{t.schedule_name} · <span className="font-mono text-xs">{t.unit}</span></span>
                  <span className={`font-mono text-xs font-bold ${STATE_CLS[t.state] || ""}`}>
                    {t.remaining != null ? `${Math.abs(t.remaining) % 1 === 0 ? Math.abs(t.remaining) : Math.abs(t.remaining).toFixed(0)} ${t.unit_label} ${t.remaining < 0 ? "over" : "left"}` : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
          {canMaint && <SchedulesManager assetId={id} schedules={schedules} onChange={() => { reloadSchedules(); load(); }} />}
          {/* Work history */}
          <div className="flex items-center justify-between mt-4 mb-2">
            <div className="font-body font-semibold text-slate-800">Service History</div>
            <Link to={`/maintenance/history?asset=${id}`} data-testid="asset-service-history-link" className="text-sm text-blue-600 font-semibold">Full history / print &amp; export →</Link>
          </div>
          {history.length ? (
            <div className="relative border-l-2 border-slate-200 ml-2 pl-4 flex flex-col gap-3">
              {history.map((w) => (
                <div key={w.id} data-testid={`history-${w.id}`} className="relative bg-white border border-slate-200 rounded p-3">
                  <span className={`absolute -left-[22px] top-4 h-3 w-3 rounded-full ${w.event_type === "fault_resolved" ? "bg-emerald-600" : w.is_safety_critical ? "bg-red-600" : "bg-blue-600"}`} />
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <span className="font-body font-semibold text-slate-900 flex items-center gap-2">
                      {w.event_type === "fault_resolved" && <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-1.5 py-0.5">Fault resolved</span>}
                      {w.work_type_name || "Work"}{w.job_title ? ` · ${w.job_title}` : ""}
                    </span>
                    <span className="font-mono text-xs text-slate-500">{fmtDate(w.work_date) || "—"}</span>
                  </div>
                  {w.description && <p className="font-body text-sm text-slate-700 mt-1">{w.description}</p>}
                  {w.faults?.length > 0 && <p className="font-body text-xs text-emerald-700 mt-1">Resolved fault{w.faults.length > 1 ? "s" : ""}: {w.faults.map((x) => x.title).join("; ")}</p>}
                  <p className="font-mono text-xs text-slate-500 mt-1">
                    {w.technician || w.performed_by_name || ""}{w.mileage != null ? ` · ${w.mileage} mi` : ""}{w.hours != null ? ` · ${w.hours} h` : ""}{w.total_cost != null ? ` · £${Number(w.total_cost).toFixed(2)}` : ""}
                  </p>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-400 text-sm p-2">No maintenance history yet.</p>}
        </TabsContent>

        <TabsContent value="checks">
          {checks.length ? checks.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded p-3 mb-2">
              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded mr-2 ${c.outcome === "fail" ? "bg-red-600 text-white" : "bg-emerald-100 text-emerald-800"}`}>{c.outcome}</span>
              <span className="font-mono text-xs text-slate-500">{fmtDateTime(c.completed_at)}</span>
            </div>
          )) : <p className="text-slate-400 text-sm p-2">No completed checks.</p>}
        </TabsContent>
        <TabsContent value="faults">
          <AssetFaultsPanel assetId={id} canJob={canMaint} canSignoff={hasPerm("supervisor", "administrator")} canReport={true} />
        </TabsContent>
        <TabsContent value="quick">
          {quick.length ? quick.map((v) => (
            <div key={v.id} data-testid={`asset-quick-${v.id}`} className="bg-white border border-slate-200 rounded p-3 mb-2 flex items-center justify-between gap-3">
              <div>
                <div className="font-body text-sm text-slate-700">{v.field_name}{v.is_safety_critical ? <span className="ml-1 text-red-600" title="Safety-critical">⚠</span> : null}</div>
                <div className="font-mono text-lg font-bold text-slate-900">{v.value} {v.unit}</div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-body bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200">{v.target_display}</span>
                  {v.inherited && <span className="text-[10px] uppercase font-bold text-slate-400">inherited</span>}
                </div>
              </div>
              <span className={`text-xs font-bold uppercase px-2 py-1 rounded shrink-0 ${v.verification_status === "verified" ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>{v.verification_status === "unverified" ? "unverified" : v.verification_status}</span>
            </div>
          )) : <p className="text-slate-400 text-sm p-2">No quick data.</p>}
        </TabsContent>

        <TabsContent value="parts">
          <AssetPartsPanel assetId={id} canManage={canManageAsset || canMaint} />
        </TabsContent>

        <TabsContent value="usage">
          <UsagePanel assetId={id} asset={asset} />
        </TabsContent>

        <TabsContent value="history">
          <div className="flex flex-col gap-2">
            {audit.length ? audit.map((r) => <AuditCard key={r.id} item={r} isAdmin={isAdmin} />) : <p className="text-slate-400 text-sm p-2">No activity recorded for this asset.</p>}
          </div>
        </TabsContent>

        <TabsContent value="eligibility">
          <EligibilityPanel assetId={id} />
        </TabsContent>

        {(canManageAsset || canMaint) && (
          <TabsContent value="requirements">
            <RequirementsManager asset={asset} />
          </TabsContent>
        )}

        {canViewTags && (
          <TabsContent value="tags">
            <AssetNfcPanel assetId={id} canManage={canManageNfc} canReplaceRevoke={canReplaceRevokeNfc} onAddTag={canManageNfc ? () => setTagDlg(true) : null} />
          </TabsContent>
        )}
      </Tabs>

      {/* Status dialog */}
      <Dialog open={statusDlg} onOpenChange={setStatusDlg}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Change Operational Status</DialogTitle></DialogHeader>
          <select data-testid="status-select" className="h-11 border-2 border-slate-300 rounded px-2" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
            <option value="">Select status…</option>{statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Textarea data-testid="status-reason" placeholder="Reason (required to clear a safety-critical status)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button data-testid="status-confirm" onClick={changeStatus} disabled={!newStatus} className="bg-slate-900 hover:bg-slate-800 font-semibold">Update Status</Button>
        </DialogContent>
      </Dialog>

      {/* RTS dialog */}
      <Dialog open={rtsDlg} onOpenChange={setRtsDlg}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Return to Service</DialogTitle></DialogHeader>
          <Textarea data-testid="rts-test-result" placeholder="Test result *" value={rts.test_result} onChange={(e) => setRts({ ...rts, test_result: e.target.value })} />
          <Textarea data-testid="rts-comments" placeholder="Comments" value={rts.comments} onChange={(e) => setRts({ ...rts, comments: e.target.value })} />
          <Button data-testid="rts-confirm" onClick={doRts} disabled={!rts.test_result} className="bg-emerald-600 hover:bg-emerald-700 font-semibold">Confirm Return to Service</Button>
        </DialogContent>
      </Dialog>

      {/* Road compliance edit */}
      {roadDlg && <RoadEditor asset={asset} onClose={() => setRoadDlg(false)} onSaved={() => { setRoadDlg(false); load(); }} />}

      {/* Full asset edit */}
      {editDlg && <AssetEditor asset={asset} onClose={() => setEditDlg(false)} onSaved={() => { setEditDlg(false); load(); }} />}

      {/* Add tag */}
      {tagDlg && <AddTagDialog assetId={id} onClose={() => setTagDlg(false)} onSaved={(created) => { setTagDlg(false); reloadTags(); if (created) setActionTag(created); }} />}
      <MoveToDialog asset={asset} open={moveDlg} onOpenChange={setMoveDlg} onMoved={load} />
      {actionTag && <TagActionsDialog tag={actionTag} open={!!actionTag} onClose={() => { setActionTag(null); reloadTags(); }} onUpdated={() => reloadTags()} />}
    </div>
  );
}

function ReadingButton({ id, asset, canForce, onDone }) {
  const [open, setOpen] = useState(false);
  const [meter, setMeter] = useState(asset.measurement_unit === "engine_hours" || asset.measurement_unit === "operating_hours" ? "hours" : asset.measurement_unit === "cycles" ? "cycles" : asset.measurement_unit === "km" ? "km" : "miles");
  const [reading, setReading] = useState("");
  const [warn, setWarn] = useState(null);

  const submit = async (extra = {}) => {
    try {
      const r = await api.post(`/assets/${id}/meter-readings`, { meter_type: meter, reading: Number(reading), ...extra });
      const st = r.data.maintenance_status;
      toast.success(st?.label ? `Reading recorded — ${st.label}` : "Reading recorded");
      setOpen(false); setReading(""); setWarn(null); onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code) setWarn(d);
      else toast.error(d?.message || d || "Failed");
    }
  };

  return (
    <>
      <Button data-testid="action-update-reading" onClick={() => setOpen(true)} className="h-16 text-base font-bold uppercase bg-slate-700 hover:bg-slate-600 flex-col gap-1"><Gauge size={22} weight="bold" /> Reading</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setWarn(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Update Reading</DialogTitle></DialogHeader>
          <select data-testid="reading-meter" className="h-10 border-2 border-slate-300 rounded px-2" value={meter} onChange={(e) => { setMeter(e.target.value); setWarn(null); }}>
            <option value="miles">Miles</option><option value="km">Kilometres</option><option value="hours">Hours</option><option value="cycles">Cycles</option>
          </select>
          <Input data-testid="reading-value" type="number" placeholder="Reading" value={reading} onChange={(e) => { setReading(e.target.value); setWarn(null); }} />
          {warn && (
            <div data-testid="reading-warning" className="bg-amber-50 border-l-8 border-amber-500 rounded p-3 text-sm text-slate-800">
              {warn.message}
              <div className="flex gap-2 mt-2">
                {warn.code === "reading_implausible" && <Button data-testid="reading-confirm" onClick={() => submit({ confirm: true })} className="h-9 bg-amber-600 text-white font-semibold">Confirm reading</Button>}
                {warn.code === "reading_lower" && canForce && <Button data-testid="reading-force" onClick={() => submit({ force: true })} className="h-9 bg-red-600 font-semibold">Record correction</Button>}
                {warn.code === "reading_lower" && !canForce && <span className="text-xs text-slate-500 self-center">A Supervisor or Admin must record a correction.</span>}
              </div>
            </div>
          )}
          {!warn && <Button data-testid="reading-save" onClick={() => submit()} disabled={reading === ""} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RoadEditor({ asset, onClose, onSaved }) {
  const [f, setF] = useState({
    mot_required: !!asset.mot_required, mot_expiry: asset.mot_expiry || "", mot_last_date: asset.mot_last_date || "",
    mot_reference: asset.mot_reference || "", mot_notes: asset.mot_notes || "", mot_cert_file_id: asset.mot_cert_file_id || null,
    tax_applicable: !!asset.tax_applicable, tax_due_date: asset.tax_due_date || "", sorn: !!asset.sorn, tax_notes: asset.tax_notes || "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try { await api.put(`/assets/${asset.id}`, f); toast.success("Road compliance updated"); onSaved(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">MOT &amp; Tax</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" data-testid="road-mot-required" checked={f.mot_required} onChange={(e) => set("mot_required", e.target.checked)} />MOT required</label>
          {f.mot_required && (
            <div className="grid grid-cols-2 gap-2 border-l-4 border-slate-200 pl-3">
              <div><label className="text-xs font-semibold text-slate-600">MOT expiry</label><Input data-testid="road-mot-expiry" type="date" value={f.mot_expiry || ""} onChange={(e) => set("mot_expiry", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Last MOT</label><Input data-testid="road-mot-last" type="date" value={f.mot_last_date || ""} onChange={(e) => set("mot_last_date", e.target.value)} /></div>
              <Input data-testid="road-mot-ref" placeholder="MOT test / reference" value={f.mot_reference} onChange={(e) => set("mot_reference", e.target.value)} className="col-span-2" />
              <Textarea data-testid="road-mot-notes" placeholder="MOT notes" value={f.mot_notes} onChange={(e) => set("mot_notes", e.target.value)} className="col-span-2" />
              <div className="col-span-2 flex items-center gap-3">
                <PhotoUpload category="reference" entityType="asset" entityId={asset.id} label="MOT certificate" onUploaded={(x) => set("mot_cert_file_id", x.id)} />
                {f.mot_cert_file_id && <EvidenceThumb fileId={f.mot_cert_file_id} />}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" data-testid="road-tax-applicable" checked={f.tax_applicable} onChange={(e) => set("tax_applicable", e.target.checked)} />Vehicle tax applicable</label>
          {f.tax_applicable && (
            <div className="grid grid-cols-2 gap-2 border-l-4 border-slate-200 pl-3">
              <div><label className="text-xs font-semibold text-slate-600">Tax due / expiry</label><Input data-testid="road-tax-due" type="date" value={f.tax_due_date || ""} onChange={(e) => set("tax_due_date", e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm self-end"><input type="checkbox" data-testid="road-sorn" checked={f.sorn} onChange={(e) => set("sorn", e.target.checked)} />SORN declared</label>
              <Textarea data-testid="road-tax-notes" placeholder="Tax notes" value={f.tax_notes} onChange={(e) => set("tax_notes", e.target.value)} className="col-span-2" />
            </div>
          )}
          <Button data-testid="road-save" onClick={save} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoadoutHistory({ vehicleId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/equipment/loadout/events?vehicle_id=${vehicleId}`).then((r) => setRows(r.data || [])).catch(() => setRows([])); }, [vehicleId]);
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid="loadout-history" className="bg-white border-2 border-slate-200 rounded-lg p-3">
      <div className="font-display font-bold text-slate-800 mb-1">Loadout history</div>
      <div className="flex flex-col gap-1">
        {rows.slice(0, 10).map((e) => (
          <div key={e.id} data-testid={`loadout-row-${e.id}`} className="text-sm border-t border-slate-100 pt-1">
            <span className="text-slate-500 font-mono text-xs">{fmtDateTime(e.created_at)}</span>{" · "}
            {e.trailer ? <span>towing <b>{e.trailer}</b></span> : <span>no trailer</span>}
            {e.items?.length > 0 && <span> · carrying {e.items.map((i) => i.label).join(", ")}</span>}
            {e.created_by && <span className="text-slate-400"> · {e.created_by}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AssetEditor({ asset, onClose, onSaved }) {
  const { hasPerm } = useAuth();
  const canTowing = hasPerm("manage_towing_data", "administrator", "asset_manager");
  const [types, setTypes] = useState([]);
  const [models, setModels] = useState([]);
  const [manus, setManus] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const NUMF = ["weight_kg", "gvw_kg", "gtw_kg", "front_axle_kg", "rear_axle_kg", "towbar_capacity_kg", "towbar_nose_kg",
    "max_braked_trailer_kg", "max_unbraked_trailer_kg", "trailer_mam_kg", "trailer_unladen_kg", "operating_weight_kg", "transport_weight_kg"];
  const [f, setF] = useState({
    asset_number: asset.asset_number || "", name: asset.name || "", asset_type_id: asset.asset_type_id || "",
    registration: asset.registration || "", serial_number: asset.serial_number || "", vin: asset.vin || "",
    model_text: asset.model_text || "", year: asset.year || "", asset_model_id: asset.asset_model_id || "",
    battery_platform_id: asset.battery_platform_id || "", assigned_person_id: asset.assigned_person_id || "",
    fuel_type: asset.fuel_type || "", havs_applicable: !!asset.havs_applicable, vibration_magnitude: asset.vibration_magnitude ?? "",
    weight_kg: asset.weight_kg ?? "", gvw_kg: asset.gvw_kg ?? "", gtw_kg: asset.gtw_kg ?? "",
    front_axle_kg: asset.front_axle_kg ?? "", rear_axle_kg: asset.rear_axle_kg ?? "",
    towbar_fitted: !!asset.towbar_fitted, towbar_capacity_kg: asset.towbar_capacity_kg ?? "", towbar_nose_kg: asset.towbar_nose_kg ?? "",
    max_braked_trailer_kg: asset.max_braked_trailer_kg ?? "", max_unbraked_trailer_kg: asset.max_unbraked_trailer_kg ?? "",
    trailer_mam_kg: asset.trailer_mam_kg ?? "", trailer_unladen_kg: asset.trailer_unladen_kg ?? "", trailer_braked: asset.trailer_braked ?? true,
    operating_weight_kg: asset.operating_weight_kg ?? "", transport_weight_kg: asset.transport_weight_kg ?? "",
    routine_checks_exempt: !!asset.routine_checks_exempt, container_asset_id: asset.container_asset_id || "",
  });
  useEffect(() => {
    api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/config/asset-models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/config/manufacturers").then((r) => setManus(r.data)).catch(() => {});
    api.get("/havs/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAllAssets(r.data || [])).catch(() => {});
  }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const manuName = (id) => manus.find((m) => m.id === id)?.name || "";
  const save = async () => {
    const payload = { ...f, year: f.year ? Number(f.year) : null, asset_model_id: f.asset_model_id || null,
      battery_platform_id: f.battery_platform_id || null,
      fuel_type: f.fuel_type || null, havs_applicable: !!f.havs_applicable,
      vibration_magnitude: f.havs_applicable && f.vibration_magnitude !== "" ? Number(f.vibration_magnitude) : null,
      container_asset_id: f.container_asset_id || null, routine_checks_exempt: !!f.routine_checks_exempt };
    NUMF.forEach((k) => { payload[k] = f[k] === "" || f[k] == null ? null : Number(f[k]); });
    if (!canTowing) { NUMF.forEach((k) => delete payload[k]); delete payload.towbar_fitted; delete payload.trailer_braked; }
    try {
      await api.put(`/assets/${asset.id}`, payload);
      toast.success("Asset updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const grp = types.find((t) => t.id === f.asset_type_id)?.group || "other";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Edit Asset</DialogTitle>
          <DialogDescription>Update identity and specification details.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-slate-600">Asset number</label><Input data-testid="edit-asset-number" value={f.asset_number} onChange={(e) => set("asset_number", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Year</label><Input data-testid="edit-asset-year" type="number" value={f.year} onChange={(e) => set("year", e.target.value)} /></div>
        </div>
        <label className="text-xs font-semibold text-slate-600">Name</label>
        <Input data-testid="edit-asset-name" value={f.name} onChange={(e) => set("name", e.target.value)} />
        <label className="text-xs font-semibold text-slate-600">Type</label>
        <select data-testid="edit-asset-type" className="h-11 border-2 border-slate-300 rounded px-2" value={f.asset_type_id} onChange={(e) => set("asset_type_id", e.target.value)}>
          <option value="">Select type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-slate-600">Registration</label><Input data-testid="edit-asset-reg" value={f.registration} onChange={(e) => set("registration", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Serial number</label><Input data-testid="edit-asset-serial" value={f.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-slate-600">VIN</label><Input data-testid="edit-asset-vin" value={f.vin} onChange={(e) => set("vin", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Model (free text)</label><Input data-testid="edit-asset-model" value={f.model_text} onChange={(e) => set("model_text", e.target.value)} /></div>
        </div>
        <label className="text-xs font-semibold text-slate-600">Model (catalogue) <span className="font-normal text-slate-400">— links this asset to a Model so model / manufacturer Quick Data applies to it</span></label>
        <select data-testid="edit-asset-model-catalogue" className="h-11 border-2 border-slate-300 rounded px-2" value={f.asset_model_id} onChange={(e) => set("asset_model_id", e.target.value)}>
          <option value="">Not linked to a catalogue model</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}{m.variant ? ` ${m.variant}` : ""}{manuName(m.manufacturer_id) ? ` · ${manuName(m.manufacturer_id)}` : ""}</option>)}
        </select>
        <label className="text-xs font-semibold text-slate-600">Battery platform (HAVS) <span className="font-normal text-slate-400">— set for cordless-tool batteries so scanning shows relevant HAVS tools</span></label>
        <select data-testid="edit-asset-battery-platform" className="h-11 border-2 border-slate-300 rounded px-2" value={f.battery_platform_id} onChange={(e) => set("battery_platform_id", e.target.value)}>
          <option value="">Not a battery</option>
          {platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <label className="text-xs font-semibold text-slate-600">Assigned to (person)</label>
        <div data-testid="edit-asset-assignee" className="text-sm text-slate-700 border-2 border-slate-200 rounded px-2 py-2 bg-slate-50">{asset.assigned_person_name || "Not assigned"} <span className="text-xs text-slate-400">— change it with “Offer custody to someone” on the asset page (the recipient must accept)</span></div>
        <label className="text-xs font-semibold text-slate-600">Fuel type <span className="font-normal text-slate-400">— shown to drivers on the tag screen</span></label>
        <select data-testid="fuel-select" className="h-11 border-2 border-slate-300 rounded px-2" value={f.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}>
          <option value="">Not set…</option>
          {FUEL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm font-semibold mt-1 text-slate-700">
          <input type="checkbox" data-testid="havs-applicable" checked={f.havs_applicable} onChange={(e) => set("havs_applicable", e.target.checked)} />
          HAVS tracked (hand-held / vibrating tool)
        </label>
        {f.havs_applicable && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Vibration magnitude (m/s²)</label>
            <Input data-testid="havs-magnitude" type="number" step="0.1" placeholder="e.g. 3.5" value={f.vibration_magnitude} onChange={(e) => set("vibration_magnitude", e.target.value)} />
          </div>
        )}
        <div data-testid="edit-weights" className="border-2 border-slate-200 rounded-lg p-3 mt-1 flex flex-col gap-2">
          <div className="font-display font-bold text-slate-800 flex items-center justify-between">
            <span>Weights & capability</span>
            {!canTowing && <span className="text-xs font-normal text-slate-400">read-only</span>}
          </div>
          {grp === "vehicle" && (
            <>
              <div><label className="text-xs font-semibold text-slate-600">Estimated Vehicle Weight (kg)</label>
                <Input data-testid="wt-weight_kg" type="number" disabled={!canTowing} value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} />
                <p className="text-[11px] text-slate-400">Estimated actual/baseline weight used for load calculations — not a plated figure.</p></div>
              <div className="grid grid-cols-2 gap-2">
                {[["gvw_kg", "Plated GVW (kg)"], ["gtw_kg", "Plated GTW (kg)"], ["front_axle_kg", "Plated front axle (kg)"], ["rear_axle_kg", "Plated rear axle (kg)"]].map(([k, l]) => (
                  <div key={k}><label className="text-xs font-semibold text-slate-600">{l}</label>
                    <Input data-testid={`wt-${k}`} type="number" disabled={!canTowing} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold mt-1 text-slate-700">
                <input type="checkbox" data-testid="wt-towbar" disabled={!canTowing} checked={f.towbar_fitted} onChange={(e) => set("towbar_fitted", e.target.checked)} /> Towbar fitted
              </label>
              {f.towbar_fitted && (
                <div className="grid grid-cols-3 gap-2">
                  {[["towbar_capacity_kg", "Max towing (kg)"], ["towbar_nose_kg", "Nose weight (kg)"], ["max_braked_trailer_kg", "Max braked trailer (kg)"], ["max_unbraked_trailer_kg", "Max unbraked (kg)"]].map(([k, l]) => (
                    <div key={k}><label className="text-[11px] font-semibold text-slate-600">{l}</label>
                      <Input data-testid={`wt-${k}`} type="number" disabled={!canTowing} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
                  ))}
                </div>
              )}
            </>
          )}
          {grp === "trailer" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-slate-600">Estimated Empty Weight (kg)</label>
                <Input data-testid="wt-trailer_unladen_kg" type="number" disabled={!canTowing} value={f.trailer_unladen_kg} onChange={(e) => set("trailer_unladen_kg", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Plated MAM / gross (kg)</label>
                <Input data-testid="wt-trailer_mam_kg" type="number" disabled={!canTowing} value={f.trailer_mam_kg} onChange={(e) => set("trailer_mam_kg", e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" disabled={!canTowing} checked={!!f.trailer_braked} onChange={(e) => set("trailer_braked", e.target.checked)} /> Braked trailer</label>
              {f.trailer_mam_kg && f.trailer_unladen_kg && <p className="col-span-2 text-[11px] text-slate-500">Derived payload ≈ {(Number(f.trailer_mam_kg) - Number(f.trailer_unladen_kg)).toLocaleString()} kg</p>}
            </div>
          )}
          {grp === "plant" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-slate-600">Estimated Weight (kg)</label>
                <Input data-testid="wt-weight_kg" type="number" disabled={!canTowing} value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Plated / max operating weight (kg)</label>
                <Input data-testid="wt-operating_weight_kg" type="number" disabled={!canTowing} value={f.operating_weight_kg} onChange={(e) => set("operating_weight_kg", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Transport weight (kg)</label>
                <Input data-testid="wt-transport_weight_kg" type="number" disabled={!canTowing} value={f.transport_weight_kg} onChange={(e) => set("transport_weight_kg", e.target.value)} /></div>
            </div>
          )}
          {(grp === "tool" || grp === "other") && (
            <div><label className="text-xs font-semibold text-slate-600">Estimated Weight (kg) <span className="font-normal text-slate-400">— optional</span></label>
              <Input data-testid="wt-weight_kg" type="number" disabled={!canTowing} value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} /></div>
          )}
        </div>

        <div><label className="text-xs font-semibold text-slate-600">Contained in (lives in) <span className="font-normal text-slate-400">— its current parent/container asset</span></label>
          <select data-testid="edit-container" className="h-11 border-2 border-slate-300 rounded px-2 w-full" value={f.container_asset_id} onChange={(e) => set("container_asset_id", e.target.value)}>
            <option value="">Not contained in another item</option>
            {allAssets.filter((x) => x.id !== asset.id).map((x) => <option key={x.id} value={x.id}>{x.asset_number} — {x.name}</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" data-testid="edit-routine-exempt" checked={f.routine_checks_exempt} onChange={(e) => set("routine_checks_exempt", e.target.checked)} />
          Routine Check: None <span className="font-normal text-slate-400">— this item needs no routine check</span>
        </label>

        <Button data-testid="edit-asset-save" onClick={save} disabled={!f.asset_number || !f.name} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function UsagePanel({ assetId, asset }) {
  const [rows, setRows] = useState(null);
  const [meter, setMeter] = useState("");
  const load = useCallback(() => api.get(`/assets/${assetId}/meter-readings`).then((r) => setRows(r.data)).catch(() => setRows([])), [assetId]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return <p className="text-slate-400 text-sm">Loading usage…</p>;
  const meters = [...new Set(rows.map((r) => r.meter_type))];
  const filtered = meter ? rows.filter((r) => r.meter_type === meter) : rows;
  const unitLabel = { miles: "mi", km: "km", hours: "h", cycles: "cyc" };
  const csv = () => {
    const cols = ["Date", "Meter", "Reading", "Source", "Recorded by", "Correction", "Linked check", "Linked job", "Note"];
    const data = filtered.map((r) => [fmtDateTime(r.reading_at), r.meter_type, r.reading, r.source, r.entered_by_name || "", r.is_correction ? "yes" : "", r.linked_check_id || "", r.linked_job_id || "", r.note || ""]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const url = URL.createObjectURL(new Blob([[cols, ...data].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${asset.asset_number}-usage-log.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="flex flex-col gap-3" data-testid="usage-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-500">Current:</span>
          {asset.current_mileage != null && <span data-testid="usage-current-miles" className="font-mono font-bold text-slate-800">{asset.current_mileage} mi</span>}
          {asset.current_hours != null && <span data-testid="usage-current-hours" className="font-mono font-bold text-slate-800">{asset.current_hours} h</span>}
          {asset.current_mileage == null && asset.current_hours == null && <span className="text-slate-400">no reading yet</span>}
        </div>
        <div className="flex items-center gap-2">
          {meters.length > 1 && (
            <select data-testid="usage-meter-filter" value={meter} onChange={(e) => setMeter(e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
              <option value="">All meters</option>{meters.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <Button data-testid="usage-export" onClick={csv} disabled={filtered.length === 0} className="h-9 bg-slate-900 hover:bg-slate-700 font-semibold text-sm">Download CSV</Button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-slate-400 text-sm">No readings logged yet. Mileage/hours are captured from driver checks, the Reading button, and maintenance jobs — and drive the maintenance intervals.</p>
      ) : (
        <div className="border-2 border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="usage-table">
            <thead className="bg-slate-50 text-left"><tr>
              <th className="p-2 font-semibold">Date</th><th className="p-2 font-semibold">Reading</th>
              <th className="p-2 font-semibold">Source</th><th className="p-2 font-semibold">Recorded by</th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`usage-row-${r.id}`} className={`border-t border-slate-100 ${r.superseded ? "opacity-50 line-through" : ""}`}>
                  <td className="p-2 font-mono text-xs text-slate-600">{fmtDateTime(r.reading_at)}</td>
                  <td className="p-2 font-mono font-bold text-slate-900">{r.reading} {unitLabel[r.meter_type] || r.meter_type}{r.is_correction ? " · correction" : ""}</td>
                  <td className="p-2"><span className="text-xs uppercase font-semibold text-slate-500">{(r.source || "").replace(/_/g, " ")}</span></td>
                  <td className="p-2 text-xs text-slate-600">{r.entered_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AddTagDialog({ assetId, onClose, onSaved }) {
  const [f, setF] = useState({ position: "", media: "qr", description: "" });
  const save = async () => {
    try { const r = await api.post("/tags", { target_type: "asset", target_id: assetId, ...f }); toast.success("Tag created"); onSaved(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display">Add NFC / QR Tag</DialogTitle></DialogHeader>
        <p className="font-body text-sm text-slate-500">This tag will be linked to this asset. Multiple tags per asset are supported.</p>
        <Input data-testid="addtag-position" placeholder="Tag position / location (e.g. Driver Cab, Service Panel)" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} />
        <select data-testid="addtag-media" className="h-10 border-2 border-slate-300 rounded px-2" value={f.media} onChange={(e) => setF({ ...f, media: e.target.value })}>
          <option value="qr">QR</option><option value="nfc">NFC</option><option value="both">NFC + QR</option>
        </select>
        <Input data-testid="addtag-notes" placeholder="Notes (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <Button data-testid="addtag-save" onClick={save} className="bg-blue-600 hover:bg-blue-700 font-semibold">Generate Tag</Button>
      </DialogContent>
    </Dialog>
  );
}

function SchedulesManager({ assetId, schedules, onChange }) {
  const [name, setName] = useState("");
  const [trigForm, setTrigForm] = useState({});
  const [editingSid, setEditingSid] = useState(null);
  const [editName, setEditName] = useState("");

  const addSchedule = async () => {
    if (!name) return;
    try { await api.post("/maintenance/schedules", { asset_id: assetId, name, is_active: true }); toast.success("Schedule added"); setName(""); onChange(); }
    catch (e) { toast.error("Failed"); }
  };
  const addTrigger = async (sid) => {
    const t = trigForm[sid] || {};
    if (!t.trigger_unit || !t.interval_value) { toast.error("Unit and interval required"); return; }
    try {
      const payload = { trigger_unit: t.trigger_unit, interval_value: Number(t.interval_value) };
      if (t.warn) payload.warn_thresholds = t.warn.split(",").map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n));
      if (t.trigger_unit === "calendar") { if (t.next_due_date) payload.next_due_date = t.next_due_date; }
      else if (t.next_due_value) payload.next_due_value = Number(t.next_due_value);
      await api.post(`/maintenance/schedules/${sid}/triggers`, payload);
      toast.success("Trigger added"); setTrigForm((p) => ({ ...p, [sid]: {} })); onChange();
    } catch (e) { toast.error("Failed"); }
  };
  const delTrigger = async (tid) => { try { await api.delete(`/maintenance/triggers/${tid}`); toast.success("Removed"); onChange(); } catch (e) { toast.error("Failed"); } };
  const delSchedule = async (sid) => { if (!window.confirm("Delete this schedule and all its triggers?")) return; try { await api.delete(`/maintenance/schedules/${sid}`); toast.success("Schedule deleted"); onChange(); } catch (e) { toast.error("Failed"); } };
  const saveName = async (sid) => { try { await api.put(`/maintenance/schedules/${sid}`, { name: editName }); toast.success("Renamed"); setEditingSid(null); onChange(); } catch (e) { toast.error("Failed"); } };
  const setTf = (sid, k, v) => setTrigForm((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [k]: v } }));

  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-3 mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="font-body font-semibold text-slate-800">Service Schedules</span>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">A schedule is a recurring service (e.g. "Full Service"). Give it one or more <b>triggers</b> — the thing that makes it due. If a schedule has several triggers it becomes due on whichever comes first.</p>
      {schedules.map((s) => (
        <div key={s.id} data-testid={`schedule-${s.id}`} className="border border-slate-200 rounded p-2 mb-2">
          <div className="flex items-center justify-between gap-2">
            {editingSid === s.id ? (
              <div className="flex items-center gap-1 flex-1">
                <Input data-testid={`edit-schedule-name-${s.id}`} className="h-8 text-sm" value={editName} onChange={(e) => setEditName(e.target.value)} />
                <Button data-testid={`save-schedule-${s.id}`} onClick={() => saveName(s.id)} className="h-8 bg-emerald-600 text-xs font-semibold">Save</Button>
                <button onClick={() => setEditingSid(null)} className="text-slate-400 text-xs px-1">Cancel</button>
              </div>
            ) : (
              <div className="font-body font-semibold text-sm text-slate-800">{s.name || "Service"}</div>
            )}
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`rename-schedule-${s.id}`} onClick={() => { setEditingSid(s.id); setEditName(s.name || ""); }} className="text-slate-500"><PencilSimple size={14} weight="bold" /></button>
              <button data-testid={`del-schedule-${s.id}`} onClick={() => delSchedule(s.id)} className="text-red-600"><Trash size={14} weight="bold" /></button>
            </div>
          </div>
          <div className="flex flex-col gap-1 mt-1">
            {(s.triggers || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between text-xs font-mono bg-slate-50 rounded px-2 py-1">
                <span>Every {t.interval_value} {t.trigger_unit === "calendar" ? "months" : t.trigger_unit}{t.warn_thresholds?.length ? ` (warn ${t.warn_thresholds.join("/")})` : ""} · next {t.next_due_value ?? t.next_due_date ?? "—"}</span>
                <button data-testid={`del-trigger-${t.id}`} onClick={() => delTrigger(t.id)} className="text-red-600"><Trash size={14} weight="bold" /></button>
              </div>
            ))}
            {(s.triggers || []).length > 1 && <div className="text-[10px] uppercase font-bold text-slate-400">Whichever comes first</div>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 mt-2">
            <select data-testid={`trig-unit-${s.id}`} className="h-9 border border-slate-300 rounded px-1 text-xs" value={(trigForm[s.id] || {}).trigger_unit || ""} onChange={(e) => setTf(s.id, "trigger_unit", e.target.value)}>
              <option value="">Unit…</option><option value="miles">miles</option><option value="km">km</option><option value="hours">hours</option><option value="cycles">cycles</option><option value="calendar">calendar (months)</option>
            </select>
            <Input data-testid={`trig-interval-${s.id}`} className="h-9 text-xs" type="number" placeholder="Interval" value={(trigForm[s.id] || {}).interval_value || ""} onChange={(e) => setTf(s.id, "interval_value", e.target.value)} />
            <Input data-testid={`trig-warn-${s.id}`} className="h-9 text-xs" placeholder="Warn ahead (e.g. 30)" value={(trigForm[s.id] || {}).warn || ""} onChange={(e) => setTf(s.id, "warn", e.target.value)} />
            {(trigForm[s.id] || {}).trigger_unit === "calendar"
              ? <Input data-testid={`trig-nextdate-${s.id}`} className="h-9 text-xs" type="date" value={(trigForm[s.id] || {}).next_due_date || ""} onChange={(e) => setTf(s.id, "next_due_date", e.target.value)} />
              : <Input data-testid={`trig-nextval-${s.id}`} className="h-9 text-xs" type="number" placeholder="Next due reading" value={(trigForm[s.id] || {}).next_due_value || ""} onChange={(e) => setTf(s.id, "next_due_value", e.target.value)} />}
            <Button data-testid={`add-trigger-${s.id}`} onClick={() => addTrigger(s.id)} className="h-9 bg-slate-700 font-semibold text-xs">Add trigger</Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1"><b>Interval</b>: how often (e.g. 250 hours, or 12 for calendar months). <b>Warn ahead</b>: how far before due to flag amber — a number in the same unit (e.g. 30 days / 500 miles); comma-separate for staged warnings like 90,60,30. Optional. <b>Next due</b>: for hours/miles/cycles enter the <b>meter reading</b> it's next due at (not a date); calendar triggers use a date.</p>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <Input data-testid="new-schedule-name" placeholder="New schedule name (e.g. Full Service)" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
        <Button data-testid="add-schedule" onClick={addSchedule} className="h-9 bg-slate-900 font-semibold"><Plus size={14} weight="bold" className="mr-1" />Add</Button>
      </div>
    </div>
  );
}
